import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { ExpensesApis } from 'apis/ExpensesApis';
import {
  IGetAllExpenses,
  IGetSumExpenses,
} from 'apis/GraphQL/Variables/expenses';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import usePagination from 'hooks/usePagination';
import { ITable } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import AddRouteCost from 'pages/Accountant/RouteCost/AddRouteCost';
import FilterRouteCost, {
  MAX_COST,
  MIN_COST,
} from 'pages/Accountant/RouteCost/FilterRouteCost';
import TableRouteCost from 'pages/Accountant/RouteCost/TableRouteCost';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  initDataTable,
  URL_IMPORT_TEMPLATE_FILE,
} from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { formatCurrency } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IMPORT_ROUTE_COST } from 'utils/constants/importId';
const { PlusOutlined } = icons;

export interface ISumExpenses {
  actualExpenses: number;
  policyExpenses: number;
  finalExpenses: number;
}

export interface IRouteCost {
  id: string;
  startDate: string;
  expensesType: string;
  policyExpenses: null;
  actualExpenses: number;
  numberPerson: number;
  finalExpenses: number;
  note: null;
  texpensesType: TexpensesType;
  tuser: Tuser;
  troute: Troute;
  tteamSales: TteamSales;
  tcustomer: Tcustomer;
  createDate: string;
  tcreateUser: User;
  modifyDate: string;
  tmodifyUser: User;
}

export interface User {
  id: string;
  fullName: string;
}

export interface Tcustomer {
  id: string;
  sapCustomerId: string;
  storeName: string;
}

export interface TexpensesType {
  id: string;
  name: string;
  expensesAmount: number;
  tunit: Troute;
}

export interface Troute {
  id: string;
  name: string;
}

export interface TteamSales {
  id: string;
  subId: string;
  name: string;
  plantId: string;
}

export interface Tuser {
  employeeId: string;
  fullName: string;
}

const RouteCostPage = () => {
  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [data, setData] = useState<ITable<IRouteCost>>(initDataTable);
  const [sumExpenses, setSumExpenses] = useState<ISumExpenses>({
    actualExpenses: 0,
    policyExpenses: 0,
    finalExpenses: 0,
  });
  const [isOpenImportModal, setIsOpenImportModal] = useState(false);
  const tableRef = useRef<any>(null);
  const [filter, setFilter] = useState<IGetSumExpenses>({
    fromPrice: MIN_COST,
    toPrice: MAX_COST,
  });
  const [openAddModal, setOpenAddModal] = useState(false);
  const navigate = useNavigate();

  const defaultFilter = useMemo(() => {
    const userAny = user as any;

    if (user?.plantCode === '5000') return {};

    if (user?.isSale) {
      const teamSale = userAny?.teamSales?.[0];

      return {
        plantId: userAny?.plantCode,
        teamSalesId: teamSale?.id || 'SALE_ID',
        teamSalesSubId: teamSale?.subId || 'SALE_SUB_ID',
        teamSalesName: teamSale?.name,
      };
    }

    return {
      plantId: userAny?.plantCode,
    };
  }, [user]);
  const { page, page_size } = usePagination();

  const { mutate: getAllExpenses, isPending: getAllExpensesLoading } =
    useMutation({
      mutationFn: (params: IGetAllExpenses) =>
        ExpensesApis.getAllExpenses(params),
      onSuccess: (data, variables) => {
        const result = data.data?.getAllExpenses;

        if (!data.data?.getAllExpenses?.items) return;

        if (variables.type === 'export') {
          exportExcel_v2(
            variables.headerRow,
            result.items || [],
            'Kế toán/Thủ quỹ - Chi phí tuyến',
          );

          return;
        }

        setData(result);
      },
    });

  const { mutate: getSumExpenses } = useMutation({
    mutationFn: (params: IGetSumExpenses) =>
      ExpensesApis.getSumExpenses(params),
    onSuccess: (data) => {
      const result = data.data?.getSumExpenses?.data || {
        actualExpenses: 0,
        policyExpenses: 0,
        finalExpenses: 0,
      };

      setSumExpenses(result);
    },
  });

  const handleExportExcel = () => {
    if (tableRef.current) tableRef.current.exportExcel();
  };

  const handleImportExcel = () => {
    setIsOpenImportModal(true);
  };

  const refetch = useCallback(
    async (isExport?: boolean, headerRow?: any) => {
      try {
        if (isExport || page === 1) {
          getAllExpenses({
            skip: 0,
            take: isExport ? data?.pageInfo?.totalItems || 10 : 10,
            ...(isExport && {
              headerRow,
              type: 'export',
            }),
            ...filter,
            ...(defaultFilter.plantId && { plantId: defaultFilter.plantId }),
            ...(defaultFilter.teamSalesId && {
              teamSaleId: defaultFilter.teamSalesId,
            }),
          });
          getSumExpenses({
            ...filter,
            ...(defaultFilter.plantId && { plantId: defaultFilter.plantId }),
            ...(defaultFilter.teamSalesId && {
              teamSaleId: defaultFilter.teamSalesId,
            }),
          });
        } else navigate(`?page=1&page_size=${page_size}`);
      } catch (error) {
        console.log('mutateGetTHTT', error);
      }
    },
    [
      data?.pageInfo?.totalItems,
      defaultFilter.plantId,
      defaultFilter.teamSalesId,
      filter,
      getAllExpenses,
      getSumExpenses,
      navigate,
      page,
      page_size,
    ],
  );

  const handleFilter = (values: IGetSumExpenses) => {
    setFilter(values);
    navigate(`?page=1&page_size=${page_size}`);
  };

  const handleCancel = () => {
    setFilter({ fromPrice: MIN_COST, toPrice: MAX_COST });
    navigate(`?page=1&page_size=${page_size}`);
  };

  const showAddModal = () => {
    setOpenAddModal(true);
  };

  const hideAddModal = () => {
    setOpenAddModal(false);
  };

  useEffect(() => {
    setTitle('Chi phí tuyến');
    setBreadcrumb([{ title: 'Kế toán/Thủ quỹ' }, { title: 'Chi phí tuyến' }]);
  }, [setTitle, setBreadcrumb]);

  useEffect(() => {
    const skip = (page - 1) * page_size || 0;
    const take = page_size || 10;

    getAllExpenses({
      skip,
      take,
      ...filter,
      ...(defaultFilter.plantId && { plantId: defaultFilter.plantId }),
      ...(defaultFilter.teamSalesId && {
        teamSaleId: defaultFilter.teamSalesId,
      }),
    });
    getSumExpenses({
      ...filter,
      ...(defaultFilter.plantId && { plantId: defaultFilter.plantId }),
      ...(defaultFilter.teamSalesId && {
        teamSaleId: defaultFilter.teamSalesId,
      }),
    });
  }, [
    defaultFilter.plantId,
    defaultFilter.teamSalesId,
    filter,
    getAllExpenses,
    getSumExpenses,
    page,
    page_size,
  ]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport onClick={handleExportExcel} />
        </Col>
        <Col>
          <ButtonImport onClick={handleImportExcel} />
        </Col>
        {user?.isSale && (
          <Col>
            <Button
              fill
              iconBefore={<PlusOutlined />}
              title="Tạo"
              handleOnclick={showAddModal}
            />
          </Col>
        )}
      </Row>

      <FilterRouteCost
        defaultFilter={defaultFilter}
        isOpen={isOpenFilter}
        onFinish={handleFilter}
        onCancel={handleCancel}
      />

      <Content>
        Tổng chi phí:&nbsp;
        <b>
          {formatCurrency({
            value: sumExpenses.finalExpenses || 0,
          })}{' '}
          VND
        </b>
      </Content>

      <TableRouteCost
        defaultFilter={defaultFilter}
        loading={getAllExpensesLoading}
        ref={tableRef}
        data={data}
        refetch={refetch}
      />

      <UploadModal
        okTitle="Import"
        importId={IMPORT_ROUTE_COST}
        open={isOpenImportModal}
        title={'Import'}
        setOpen={setIsOpenImportModal}
        templateFileName="Kế toán/Thủ quỹ - Chi phí tuyến"
        refetch={refetch}
      />

      <AddRouteCost
        defaultFilter={defaultFilter}
        open={openAddModal}
        onClose={hideAddModal}
        refetch={refetch}
      />
    </>
  );
};

export default RouteCostPage;
